import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import httpClient from '../../../httpClient';
import ChevronIcon from '../../../images/chevron-down-12.svg';
import styles from '../../../pages/ProductSelection.module.css';
import useProductSelectionStore from '../../../stores/useProductSelectionStore';


const CIRCLE_OVAL_FRAME_PATTERN = /\b(Circle|Oval)\s+Frame\b/i;
const isCircleOrOvalFrameType = (text) => CIRCLE_OVAL_FRAME_PATTERN.test(text || '');

const MAT_UPSTREAM_KEYS = new Set(['frameType', 'frame', 'frameMat']);

const MAT_TYPE_LABELS = {
  'no-mat': 'No Mat',
  '8-ply': '8-Ply',
  'double-mat-4-ply': 'Double Mat 4-Ply',
};

const MAT_TYPE_PATTERNS = {
  'no-mat': /no[\s-]*mat/i,
  '8-ply': /8[\s-]*ply/i,
  'double-mat-4-ply': /double.*4[\s-]*ply/i,
};

const getServiceMatNameSuffix = (service) => {
  const nameParts = service?.attributes?.name?.split('|');
  if (nameParts && nameParts.length > 1) {
    return nameParts[nameParts.length - 1].trim();
  }
  return service?.attributes?.frameMatName;
};

const deriveMatTypeKey = (matNameSuffix) => {
  if (!matNameSuffix) return undefined;
  return Object.keys(MAT_TYPE_PATTERNS).find((key) =>
    MAT_TYPE_PATTERNS[key].test(matNameSuffix),
  );
};

// circle and oval print sizes
const CIRCLE_OVAL_PRINT_SIZES = new Set([
  '5x5',
  '8x8',
  '10x10',
  '12x12',
  '16x16',
  '7x10',
  '11x14',
  '10x13',
  '14x17',
]);

const normalizePrintSizeName = (text) =>
  (text || '')
    .replace(/\s*\(\$[^)]*\)\s*$/, '') 
    .toLowerCase()
    .replace(/[”"]/g, '') 
    .replace(/×/g, 'x')
    .trim();

const isCircleOvalPrintSize = (option) => {
  const name = normalizePrintSizeName(option?.text);
  if (!name) return false;
  if (name.includes('circle deckle')) {
    return /^(8|12)\b/.test(name);
  }else if (name.includes('oval deckle')) {
    return /^(7|10)\b/.test(name);
  }
  const token = name.replace(/\bprints?\b/g, '').replace(/\s+/g, '');
  return CIRCLE_OVAL_PRINT_SIZES.has(token);
};

const CircleOvalFramingFormInputs = ({ quantityRef }) => {
  const {
    form,
    setForm,
    resetPrintSizeForm,
    setPrintSizeLoading,
    processPrintSizeData,
    setFinishingServiceLoading,
    setFrameInputsLoading,
    processFinishingCategoriesData,
    processFinishingServicesData,
    processFinishingServiceChange,
    processFrameMatSelection,
    getBaseMeta,
    resetFormInputs,
    getMetaResets,
    setIsEditInitializing,
    finishingCategoriesData,
  } = useProductSelectionStore();

  console.log("form inputs",form?.inputs)
  const paperTypeInput = form?.inputs?.paperType;
  const printSizeInput = form?.inputs?.printSize;
  const finishingServiceInput = form?.inputs?.finishingService;
  const frameTypeInput = form?.inputs?.frameType;
  const matTypeInput = form?.inputs?.frameMat;
  const acrylicInput = form?.inputs?.acrylic;

  const paperTypeSelectedValue = paperTypeInput?.selectedValue;
  const printSizeSelectedValue = printSizeInput?.selectedValue;
  const finishingCategoryId = form?.meta?.finishingCategoryId;
  // 'circle' or 'oval', derived from the selected print size (see the
  // printSizeInput effect below) - needed to resolve the correct
  // frame_mat_pricing row (mat_type + opening_shape) for the payload.
  const matOpeningShape = form?.meta?.matOpeningShape;

  const circleOvalFrameTypeOptions = (frameTypeInput?.options || []).filter(
    (option) => isCircleOrOvalFrameType(option?.text),
  );

  const getCircleOvalCropShape = (printSizeText) => {
    if (!printSizeText) return undefined;
    const match = printSizeText.match(/(\d+)\s*x\s*(\d+)/i);
    if (!match) return undefined;
    const width = Number(match[1]);
    const height = Number(match[2]);
    return width === height ? 'circle' : 'oval';
  };

  // --- Queries ---

  const printSizesQuery = useQuery({
    queryKey: ['printSizes', paperTypeSelectedValue],
    queryFn: () => httpClient.get(`products/${paperTypeSelectedValue}/print_sizes`),
    enabled: !!paperTypeSelectedValue && paperTypeSelectedValue !== 'default',
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => {
      processPrintSizeData(response);
    },
  });

  const finishingCategoriesQuery = useQuery({
    queryKey: ['finishingCategories', printSizeSelectedValue],
    queryFn: () =>
      httpClient.get(
        `products/print_sizes/${printSizeSelectedValue}/finishing_categories`,
      ),
    enabled: !!printSizeSelectedValue && printSizeSelectedValue !== 'default',
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => {
      processFinishingCategoriesData(response);
    },
  });

  const finishingServicesQuery = useQuery({
    queryKey: ['finishingServices', printSizeSelectedValue, finishingCategoryId],
    queryFn: () =>
      httpClient.get(
        `products/print_sizes/${printSizeSelectedValue}/finishing_categories/${finishingCategoryId}/finishing_services`,
      ),
    enabled: !!printSizeSelectedValue && !!finishingCategoryId,
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => {
      processFinishingServicesData(response);
    },
  });

  // Frame Mat options, sourced from the backend frame_mats reference list
  // rather than parsed out of finishing service names. Mat opening shape
  // (also returned by this endpoint) is not used here - circle/oval mat
  // opening shape is derived from print size instead (see
  // getCircleOvalCropShape / the printSizeInput effect below).
  const frameMatsQuery = useQuery({
    queryKey: ['frameMats', printSizeSelectedValue, finishingCategoryId],
    queryFn: () =>
      httpClient.get(
        `products/frame_mats?print_size_guid=${printSizeSelectedValue}&finishing_category_guid=${finishingCategoryId}`,
      ),
    enabled: !!finishingCategoryId && !!printSizeSelectedValue,
    staleTime: Infinity,
    cacheTime: 0,
  });

  // Only offer a mat type that actually has a priced finishing service for
  // the current print size + frame category, so a selection never dead-ends
  // with no matching Acrylic option.
  const availableMatTypeKeys = new Set(
    (finishingServicesQuery.data?.data || [])
      .map((service) => deriveMatTypeKey(getServiceMatNameSuffix(service)))
      .filter(Boolean),
  );

  const frameMatRows = frameMatsQuery.data?.data ?? [];
  // Only rows for the current circle/oval opening shape apply - each mat
  // type exists once per shape, so this also pins down which row's guid is
  // the correct frame_mat_pricing_id for the payload.
  const frameMatRowsForShape = frameMatRows.filter(
    (row) => row.attributes.openingShape === matOpeningShape,
  );
  const pricedFrameMatOptions = Array.from(
    new Set(frameMatRowsForShape.map((row) => row.attributes.matType)),
  )
    .filter((matType) => availableMatTypeKeys.has(matType))
    .map((matType) => ({
      value: matType,
      text: MAT_TYPE_LABELS[matType] || matType,
    }));
  const frameMatOptions = availableMatTypeKeys.has('no-mat')
    ? [{ value: 'no-mat', text: MAT_TYPE_LABELS['no-mat'] }, ...pricedFrameMatOptions]
    : pricedFrameMatOptions;
  const hasOnlyNoMat =
    frameMatOptions.length === 1 && frameMatOptions[0].value === 'no-mat';

  const selectedFrameMatPricing = frameMatRowsForShape.find(
    (row) => row.attributes.matType === matTypeInput?.selectedValue,
  );
  const frameMatPricingGuid = selectedFrameMatPricing?.id;

  // Persist the resolved frame_mat_pricing guid so it goes out in the line
  // item payload as `frame_mat` (mirrors FramingFormInputs.jsx).
  useEffect(() => {
    if (form?.meta?.frameMatPricingGuid === frameMatPricingGuid) return;
    setForm((prev) => ({
      ...prev,
      meta: { ...prev.meta, frameMatPricingGuid },
    }));
  }, [frameMatPricingGuid]);

  useEffect(() => {
    if (!hasOnlyNoMat || matTypeInput?.selectedValue === 'no-mat') return;
    setForm((prev) => ({
      ...prev,
      inputs: {
        ...prev.inputs,
        frameMat: {
          ...prev.inputs.frameMat,
          selectedValue: 'no-mat',
          selectedValueText: MAT_TYPE_LABELS['no-mat'],
        },
      },
    }));
  }, [hasOnlyNoMat, matTypeInput?.selectedValue, setForm]);

  // --- Loading effects ---

  useEffect(() => {
    if (paperTypeSelectedValue === 'default') {
      resetPrintSizeForm();
    } else if (
      paperTypeSelectedValue &&
      paperTypeSelectedValue !== 'default' &&
      printSizesQuery.isLoading
    ) {
      setPrintSizeLoading(true);
    }
  }, [paperTypeSelectedValue, printSizesQuery.isLoading]);

  useEffect(() => {
    if (
      printSizeSelectedValue &&
      printSizeSelectedValue !== 'default' &&
      finishingCategoriesQuery.isLoading
    ) {
      setFinishingServiceLoading(true);
    }
  }, [printSizeSelectedValue, finishingCategoriesQuery.isLoading]);

  useEffect(() => {
    if (
      finishingCategoryId &&
      finishingCategoryId !== 'default' &&
      finishingServicesQuery.isLoading
    ) {
      setFrameInputsLoading(true);
    }
  }, [finishingCategoryId, finishingServicesQuery.isLoading]);

  useEffect(() => {
    const options = finishingServiceInput?.options;
    if (!options || options.length === 0) return;
    if (finishingServiceInput?.selectedValue) return;

    const framingOption = options.find((opt) => opt?.text === 'Framing');
    if (!framingOption) return;

    setForm((prev) => ({
      ...prev,
      inputs: {
        ...prev.inputs,
        finishingService: {
          ...prev.inputs.finishingService,
          selectedValue: framingOption.value,
          selectedValueText: framingOption.text,
        },
      },
    }));
  }, [finishingServiceInput?.options, finishingServiceInput?.selectedValue, setForm]);

  useEffect(() => {
    if (finishingServiceInput?.selectedValueText === undefined) return;
    processFinishingServiceChange(finishingServiceInput.selectedValueText);
  }, [finishingServiceInput?.selectedValueText, finishingCategoriesData]);

  // Frame Type selection sets the finishing category. There is no downstream
  // Frame Mat menu for circle and oval frames.
  useEffect(() => {
    if (frameTypeInput?.selectedValue === undefined) return;

    if (frameTypeInput.selectedValue === 'default') {
      setForm((prevState) => ({
        meta: {
          ...getBaseMeta(prevState),
          finishingCategoryId: undefined,
          finishingServiceId: undefined,
          finishingDetailId: undefined,
          requiredInputs: ['Frame Type'],
        },
        inputs: {
          paperType: prevState.inputs.paperType,
          printSize: prevState.inputs.printSize,
          finishingService: prevState.inputs.finishingService,
          frameType: prevState.inputs.frameType,
        },
      }));
      return;
    }

    setForm((prevState) => ({
      meta: {
        ...getBaseMeta(prevState),
        finishingCategoryId: frameTypeInput.selectedValue,
      },
      inputs: prevState.inputs,
    }));
  }, [frameTypeInput?.selectedValue]);

  // Frame mat selection
  useEffect(() => {
    console.log("matTypeInput?.selectedValue in select",matTypeInput?.selectedValue)
    if (matTypeInput?.selectedValue === undefined) return;
    processFrameMatSelection(
      matTypeInput.selectedValue,
      matTypeInput.selectedValueText,
    );
  }, [matTypeInput?.selectedValue]);

  // Auto-select UV Acrylic
  useEffect(() => {
    if (!acrylicInput) return;
    if (acrylicInput?.selectedValue && acrylicInput.selectedValue !== 'default') return;

    const options = acrylicInput?.options || [];
    if (options.length === 0) return;

    const firstOpt = options.find((o) => o.value);

    const value = firstOpt?.value ?? 'none';
    const text  = firstOpt?.text  ?? 'None';
    const price = firstOpt?.price;

    setForm((prev) => ({
      ...prev,
      meta: {
        ...getBaseMeta(prev),
        finishingServiceId: value !== 'none' ? value : undefined,
      },
      inputs: {
        ...prev.inputs,
        acrylic: {
          ...prev.inputs.acrylic,
          selectedValue: value,
          selectedValueText: text,
          selectedValuePrice: price,
        },
      },
    }));
  }, [acrylicInput?.options, acrylicInput?.selectedValue, setForm, getBaseMeta]);
  
  // Keep requiredInputs in sync with current selections.
  useEffect(() => {
    const isSet = (v) => v && v !== 'default';
    const paperSelected = isSet(paperTypeSelectedValue);
    const sizeSelected = isSet(printSizeSelectedValue);
    const frameTypeSelected = isSet(frameTypeInput?.selectedValue);
    const frameMatSelected = isSet(matTypeInput?.selectedValue);
    const acrylicSelected = isSet(acrylicInput?.selectedValue);

    const required = [];
    if (!paperSelected) required.push('Paper Type');
    if (!sizeSelected) required.push('Print Size');
    if (paperSelected && sizeSelected && !frameTypeSelected) {
      required.push('Frame Type');
    }
    if (frameTypeSelected && !frameMatSelected) {
      required.push('Frame Mat');
    }
    if (frameTypeSelected && frameMatSelected && !acrylicSelected) {
      required.push('Acrylic');
    }

    const current = form?.meta?.requiredInputs || [];
    const matches =
      current.length === required.length &&
      current.every((v, i) => v === required[i]);
    if (matches) return;

    setForm((prev) => ({
      ...prev,
      meta: { ...prev.meta, requiredInputs: required },
    }));
  }, [
    paperTypeSelectedValue,
    printSizeSelectedValue,
    frameTypeInput?.selectedValue,
    matTypeInput?.selectedValue,
    acrylicInput?.selectedValue,
    form?.meta?.requiredInputs,
    setForm,
  ]);

  // During edit-init, processPrintSizeData populates selectedValue but spreads
  // defaultOptions.meta (matOpeningShape: undefined). This effect re-derives the
  // shape from the print size text so the circle/oval mask appears correctly.
  useEffect(() => {
    if (!printSizeInput?.selectedValue || printSizeInput.selectedValue === 'default') return;
    const cropShape = getCircleOvalCropShape(printSizeInput.selectedValueText);
    if (!cropShape) return;
    setForm((prev) => {
      if (prev.meta.matOpeningShape === cropShape) return prev;
      return { ...prev, meta: { ...prev.meta, matOpeningShape: cropShape } };
    });
  }, [printSizeInput?.selectedValue, printSizeInput?.selectedValueText, setForm]);

  const stripPrice = (text) => text.replace(/\(\$\d+(\.\d{1,2})?\)/g, '').trim();

  const filterPaperOption = (option) => {
    if (!option?.text) return false;
    const text = option.text.toLowerCase();
    return !text.includes('canvas') && !text.includes('lustre') && !text.includes('deckled paper');
  };

  const handlePaperTypeChange = (e) => {
    const { value } = e.target;
    const selectedText = e.target.options[e.target.selectedIndex].text;

    setForm((prevState) => ({
      ...prevState,
      inputs: {
        ...prevState.inputs,
        paperType: {
          ...prevState.inputs.paperType,
          selectedValue: value,
          selectedValueText: selectedText,
        },
        printSize: {
          ...prevState.inputs.printSize,
          selectedValue: undefined,
          selectedValueText: undefined,
          selectedValuePrice: undefined,
        },
      },
    }));
  };

  const handlePrintSizeChange = (e) => {
    const { value } = e.target;
    const selectedText = e.target.options[e.target.selectedIndex].text;
    const selectedOption = (printSizeInput?.options || []).find(
      (option) => option?.value?.toString() === value,
    );

    const cropShape = getCircleOvalCropShape(selectedText);
    setIsEditInitializing(false);
    setForm((prevState) => ({
      meta: {
        ...prevState.meta,
        xAspectRatio: selectedOption?.xAspectRatio,
        yAspectRatio: selectedOption?.yAspectRatio,
        aspectRatio:
          selectedOption?.xAspectRatio && selectedOption?.yAspectRatio
            ? selectedOption.xAspectRatio / selectedOption.yAspectRatio
            : undefined,
        matOpeningShape: cropShape,
        finishingCategoryId: undefined,
        finishingServiceId: undefined,
        finishingDetailId: undefined,
        matType: undefined,
      },
      inputs: {
        paperType: prevState.inputs.paperType,
        printSize: {
          ...prevState.inputs.printSize,
          selectedValue: value,
          selectedValueText: selectedText,
          selectedValuePrice: selectedOption?.price,
        },
        finishingService: {
          ...prevState.inputs.finishingService,
          selectedValue: undefined,
          selectedValueText: undefined,
        },
      },
    }));
  };

  const handleGenericSelectChange = (key, e) => {
    const input = form?.inputs?.[key];
    if (!input) return;

    setIsEditInitializing(false);
    setForm((prevState) => {
      const resetInputs = resetFormInputs(prevState.inputs, key);
      const selectedOption = input.options.find(
        (option) => option?.value?.toString() === e.target.value,
      );

      resetInputs[key] = {
        ...resetInputs[key],
        selectedValue: e.target.value,
        selectedValueText: e.target.options[e.target.selectedIndex].text,
        selectedValuePrice: selectedOption?.price,
      };

      const newMeta = { ...prevState.meta };
      const metaResets = getMetaResets(key, prevState);
      if (metaResets.finishingCategoryId) newMeta.finishingCategoryId = undefined;
      if (metaResets.finishingServiceId) newMeta.finishingServiceId = undefined;
      if (metaResets.finishingDetailId) newMeta.finishingDetailId = undefined;
      if (MAT_UPSTREAM_KEYS.has(key)) {
        newMeta.matType = undefined;
      }

      return { meta: newMeta, inputs: resetInputs };
    });
  };

  if (!form?.inputs) return null;

  const renderSelect = ({ input, value, onChange, fallbackLabel, disabled }) => (
    <div className="input-container paper-type">
      <div className="label-container">
        <div className="div-block-150">
          <div className="sub-label-form">{input?.label || fallbackLabel}</div>
        </div>
      </div>
      <div className="form-block-input w-form">
        <form className="form-input select-field">
          <select
            className={`form-select-field w-select select-field-wrapper ${styles.selectNoNativeArrow}`}
            style={{ width: '100%' }}
            onChange={onChange}
            value={value || 'default'}
            disabled={disabled ?? input?.isDisabled}
          >
            {input?.isLoading && (input?.options?.length || 0) === 0 ? (
              <option value="">Loading...</option>
            ) : (
              <option value="default">Choose One</option>
            )}
            {(input?.options || [])
              .filter((option) => !option?.text?.endsWith('**'))
              .map((option) => {
                if (!option?.value || !option?.text) return null;
                return (
                  <option key={option.value} value={option.value}>
                    {option.text}
                  </option>
                );
              })
              .filter(Boolean)}
          </select>
          <img
            src={ChevronIcon}
            alt=""
            style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
              width: '16px',
              height: '16px',
            }}
          />
        </form>
      </div>
    </div>
  );

  return (
    <div className={styles.printsFormStack}>
      <div className="input-container quantity">
        <div className="label-container">
          <div className="div-block-150">
            <div className="sub-label-form">Quantity</div>
          </div>
        </div>
        <div className="form-block-input w-form">
          <div className="input-wrapper">
            <input
              ref={quantityRef}
              type="number"
              min="1"
              maxLength="256"
              name="field-quantity"
              defaultValue="1"
              className="text-field-2 data-jscolor w-input"
              pattern="[0-9]*"
              inputMode="numeric"
            />
          </div>
        </div>
      </div>

      {/* Paper type */}
      <div className="input-container paper-type">
        <div className="label-container">
          <div className="div-block-150">
            <div className="sub-label-form">
              {paperTypeInput?.label || 'Paper type'}
            </div>
          </div>
        </div>
        <div className="form-block-input w-form">
          <form className="form-input select-field">
            <select
              className={`form-select-field w-select select-field-wrapper ${styles.selectNoNativeArrow}`}
              style={{ width: '100%' }}
              onChange={handlePaperTypeChange}
              value={paperTypeSelectedValue || 'default'}
            >
              {paperTypeInput?.isLoading &&
              (paperTypeInput?.options?.length || 0) === 0 ? (
                <option value="">Loading...</option>
                ) : (
                  <option value="default">Choose One</option>
                )}
              {(paperTypeInput?.options || [])
                .filter(filterPaperOption)
                .filter((option) => !option.text?.endsWith('**'))
                .map((option) => (
                  <option key={option.value} value={option.value}>
                    {stripPrice(option.text)}
                  </option>
                ))}
            </select>
            <img
              src={ChevronIcon}
              alt=""
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                width: '16px',
                height: '16px',
              }}
            />
          </form>
        </div>
      </div>

      {/* Print size, restricted to circle and oval frame sizes */}
      <div className="input-container paper-type">
        <div className="label-container">
          <div className="div-block-150">
            <div className="sub-label-form">
              {printSizeInput?.label || 'Print size'}
            </div>
          </div>
        </div>
        <div className="form-block-input w-form">
          <form className="form-input select-field">
            <select
              className={`form-select-field w-select select-field-wrapper ${styles.selectNoNativeArrow}`}
              style={{ width: '100%' }}
              onChange={handlePrintSizeChange}
              value={printSizeInput?.selectedValue || 'default'}
              disabled={printSizeInput?.isDisabled}
            >
              {printSizeInput?.isLoading &&
              (printSizeInput?.options?.length || 0) === 0 ? (
                <option value="">Loading...</option>
                ) : (
                  <option value="default">Choose One</option>
                )}
              {(() => {
                const paperTypeName = (paperTypeInput?.selectedValueText || '').toLowerCase();
                const isCircleDeckleSelected = paperTypeName.includes('circle deckle');
                const isOvalDeckleSelected = paperTypeName.includes('oval deckle');
                const ovalDeckleAllowedSizes = ['7x10', '7 x 10', '10x13', '10 x 13'];

                return (printSizeInput?.options || [])
                  .filter((option) => {
                    if (option.text?.endsWith('**')) return false;
                    if (!isCircleOvalPrintSize(option)) return false;

                    const optName = option.text.toLowerCase();
                    if (isCircleDeckleSelected && (optName.includes('16x16') || optName.includes('16 x 16'))) {
                      return false;
                    }
                    if (isOvalDeckleSelected) {
                      return ovalDeckleAllowedSizes.some((size) => optName.includes(size));
                    }
                    return true;
                  })
                  .map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.text}
                    </option>
                  ));
              })()}
            </select>
            <img
              src={ChevronIcon}
              alt=""
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                width: '16px',
                height: '16px',
              }}
            />
          </form>
        </div>
      </div>

      {/* Frame Type, restricted to circle and oval options */}
      {frameTypeInput &&
        renderSelect({
          input: {
            ...frameTypeInput,
            options: circleOvalFrameTypeOptions,
          },
          value: frameTypeInput?.selectedValue,
          onChange: (e) => handleGenericSelectChange('frameType', e),
          fallbackLabel: 'Frame type',
        })}

      {/* Frame Mat - options sourced from products/frame_mats, not parsed names */}
      {matTypeInput && !hasOnlyNoMat && (() => {
        return renderSelect({
          input: { ...matTypeInput,
            options: frameMatOptions,
            isLoading: matTypeInput?.isLoading || frameMatsQuery.isLoading || finishingServicesQuery.isLoading,
          },
          value: matTypeInput?.selectedValue,
          onChange: (e) => handleGenericSelectChange('frameMat', e),
          fallbackLabel: 'Frame Mat',
        });
      })()}

      {/* Acrylic */}
      {acrylicInput && matTypeInput?.selectedValue && matTypeInput.selectedValue !== 'default' && (() => {
        const apiOptions = acrylicInput?.options || [];
        const acrylicOptions = apiOptions.length > 0
          ? apiOptions
          : [{ value: 'none', text: 'None' }];
        return renderSelect({
          input: { ...acrylicInput, options: acrylicOptions },
          value: acrylicInput?.selectedValue,
          onChange: (e) => handleGenericSelectChange('acrylic', e),
          fallbackLabel: 'Acrylic',
        });
      })()}
    </div>
  );
};

export default CircleOvalFramingFormInputs;
